import 'swagger-ui-react/swagger-ui.css';
import React, { FunctionComponent, Suspense, useMemo, useRef } from 'react';
import { Grid } from '@rbx/ui';
import { convertRobloxUrlToCurrentEnvironment } from '@modules/utilities/constants/baseUrl';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import { LegacyReferenceLayoutPlugin } from './LegacyReferenceLayoutPlugin';
import {
  processSwaggerSpec,
  getRequestAuthModeAndPath,
  buildCSRFRetryRequestConfig,
  transformFetchResponse,
  logTryItOutEvent,
  HTTP_HEADERS,
  type SwaggerUIRequest,
  type SwaggerUIResponse,
  type CachedRequestData,
} from './tryItOut';
import TryItOutAuthMode from '../types/TryItOutAuthMode';
import { getEndpointRiskLevel } from '../config/endpointRiskLevels';

type SwaggerDocsContentProps = {
  spec: string;
  defaultModelExpandDepth?: number;
  defaultModelsExpandDepth?: number;
};

// Hardcoded to prevent swagger-ui-react's zensmooth library from causing issues
// https://github.com/swagger-api/swagger-ui/issues/5978
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).noZensmooth = true;
}

const SwaggerUI = React.lazy(() => import('swagger-ui-react'));

const SwaggerDocsContent: FunctionComponent<React.PropsWithChildren<SwaggerDocsContentProps>> = ({
  spec: specStringified,
  defaultModelExpandDepth = 3,
  defaultModelsExpandDepth = 3,
}) => {
  const spec = useMemo(() => {
    if (process.env.enableTryItOut) {
      return processSwaggerSpec(specStringified);
    }
    return JSON.parse(specStringified);
  }, [specStringified]);

  const requestCache = useRef<Map<string, CachedRequestData>>(new Map());

  const requestInterceptor = (request: SwaggerUIRequest): SwaggerUIRequest => {
    const requestMethod = request.method.toUpperCase();
    const requestUrl = new URL(request.url);

    const { authMode, operationPath } = getRequestAuthModeAndPath(
      requestMethod,
      requestUrl.pathname,
    );
    request.credentials = authMode === TryItOutAuthMode.COOKIE ? 'include' : 'omit';

    const url = `${requestUrl.origin}${operationPath}`;

    const riskLevel = getEndpointRiskLevel(requestMethod, requestUrl.origin, operationPath);

    logTryItOutEvent(DocsSiteEventType.TryItOutClickExecute, {
      authMode,
      method: requestMethod,
      url,
      riskLevel,
    });

    request.url = convertRobloxUrlToCurrentEnvironment(request.url);

    // Store request info for CSRF handling using requestCache
    if (request.url) {
      requestCache.current.set(request.url, {
        method: request.method,
        body: request.body,
        credentials: request.credentials,
      });
    }

    return request;
  };

  const responseInterceptor = (
    response: SwaggerUIResponse,
  ): SwaggerUIResponse | Promise<SwaggerUIResponse> => {
    // Handle CSRF token retry logic for 403 responses
    if (response.status === 403 && response.headers) {
      const csrfToken = response.headers[HTTP_HEADERS.CSRF_TOKEN];

      if (csrfToken) {
        const cachedRequest = requestCache.current.get(response.url);
        const requestConfig = buildCSRFRetryRequestConfig(cachedRequest, csrfToken);

        return fetch(response.url, requestConfig)
          .then(transformFetchResponse)
          .catch(() => response);
      }
    }

    return response;
  };

  return (
    <Suspense fallback={<div />}>
      <Grid item container>
        <Grid item XSmall={12}>
          <div>
            <SwaggerUI
              deepLinking
              {...(process.env.enableTryItOut && {
                requestInterceptor,
                responseInterceptor,
              })}
              onComplete={() => {
                const element = window.document.querySelector(`a[href*="${window.location.hash}"]`);
                if (element !== null && window.location.hash !== '') {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              spec={spec}
              plugins={[LegacyReferenceLayoutPlugin]}
              defaultModelExpandDepth={defaultModelExpandDepth}
              defaultModelsExpandDepth={defaultModelsExpandDepth}
              layout='LegacyReferenceLayout'
            />
          </div>
        </Grid>
      </Grid>
    </Suspense>
  );
};

export default SwaggerDocsContent;
